import React, { useState, useRef, useEffect, useCallback } from 'react'
import { Switch, FormControlLabel } from '@mui/material'
import { useCandidateChat } from '../CandidateChatContext'
import { Button, Tooltip } from '../../../_atoms'
import { useTranslation } from '~/hooks/useTranslation'
import ChatAttachmentModal from './ChatAttachmentModal'

// CSS styles for auto-growing textarea
const growWrapStyles = `
  .grow-wrap {
    display: grid;
    align-self: end;
  }
  
  .grow-wrap::after {
    content: attr(data-replicated-value) " ";
    white-space: pre-wrap;
    visibility: hidden;
    border-radius: 20px;
    padding: 12px 16px;
    font: inherit;
    font-size: 16px;
    line-height: 1.4;
    min-height: 48px;
    max-height: 200px;
    box-sizing: border-box;
    grid-area: 1 / 1 / 2 / 2;
    /* Use transparent border to avoid theme conflicts */
    border: 1px solid transparent;
  }
  
  .grow-wrap > textarea {
    resize: none;
    overflow: hidden;
    border-radius: 20px;
    padding: 12px 16px;
    font: inherit;
    font-size: 16px;
    line-height: 1.4;
    min-height: 48px;
    max-height: 200px;
    box-sizing: border-box;
    grid-area: 1 / 1 / 2 / 2;
    transition: height 0.1s ease-out;
    /* Remove border styling to let Tailwind classes handle it */
    border: none;
  }
`

/**
 * TextMessageEditor component - provides text input and send functionality
 * Based on the op_next design from app/views/home/coach_chat/content/_text_message_editor.html.erb
 *
 * @param {Object} props
 * @param {string} props.chatStatus - Current chat status
 * @param {Object} props.selectedCoach - The currently selected coach
 */
const TextMessageEditor = ({ chatStatus, selectedCoach }) => {
  const {
    candidateChatManager,
    isDarkMode,
    toggleDarkMode,
    getInputClass,
    getDarkModeClasses,
    userId,
    isMobile,
  } = useCandidateChat()
  const { t } = useTranslation('coach_chat')
  const [message, setMessage] = useState('')
  const [isTyping, setIsTyping] = useState(false)
  const textareaRef = useRef(null)

  // Debug logging for props changes
  useEffect(() => {
    /*
    console.log('TextMessageEditor - Props changed:', {
      chatStatus,
      selectedCoach,
      hasChatManager: !!candidateChatManager,
    })
    */
  }, [chatStatus, selectedCoach, candidateChatManager])

  // Debug logging for chatStatus changes
  useEffect(() => {
    // console.log('TextMessageEditor - chatStatus changed:', chatStatus)
    /*
    console.log(
      'TextMessageEditor - textarea disabled:',
      chatStatus !== 'chat_active',
    )
    */
  }, [chatStatus])

  // Auto-resize textarea as content grows using CSS Grid approach
  useEffect(() => {
    if (textareaRef.current) {
      const growWrap = textareaRef.current.parentNode
      if (growWrap) {
        growWrap.dataset.replicatedValue = message
      }
    }
  }, [message])

  // Memoize the attachment upload handler to prevent unnecessary re-renders
  const handleAttachmentUploaded = useCallback(
    (attachmentData) => {
      // Handle attachment upload success - send candidate_action message
      if (candidateChatManager && attachmentData?.attachment?.shareable_url) {
        const action = {
          action_type: 'chat_attachment',
          action_target: attachmentData.attachment.shareable_url,
          attachment_name: attachmentData.attachment.name,
          attachment_id: attachmentData.attachment.id,
          attachment_size: attachmentData.attachment.file_size,
          attachment_type: attachmentData.attachment.content_type,
        }

        // Format action text for attachment
        const fileName = attachmentData.attachment.name || 'file'
        const actionText = `sent ${fileName}`

        // Create a message object to display the attachment on candidate side
        const attachmentMessage = {
          id: Date.now(),
          type: 'candidate_action',
          actionType: 'chat_attachment',
          actionData: action,
          text: actionText, // Add text field for chat history
          timestamp: new Date().toISOString(),
          sender: 'candidate',
          senderName: 'me',
          attachment: {
            name: attachmentData.attachment.name,
            size: attachmentData.attachment.file_size,
            type: attachmentData.attachment.content_type,
            url: attachmentData.attachment.shareable_url,
            id: attachmentData.attachment.id,
          },
        }

        // Add the attachment message to the messages array
        candidateChatManager.addMessage(attachmentMessage)

        // Send the candidate_action to the coach
        candidateChatManager.sendAction(action)
      }
    },
    [candidateChatManager],
  )

  // Handle message input change
  const handleMessageChange = (event) => {
    const value = event.target.value
    setMessage(value)

    // Send typing indicator when user starts typing
    if (
      value &&
      !isTyping &&
      candidateChatManager &&
      selectedCoach &&
      chatStatus === 'chat_active'
    ) {
      setIsTyping(true)

      try {
        candidateChatManager.sendTypingIndicator()
      } catch (error) {
        console.error('Error sending typing indicator:', error)
        setIsTyping(false)
        return
      }

      // Clear typing indicator after a delay
      setTimeout(() => setIsTyping(false), 3000)
    }
  }

  // Handle sending message
  const handleSendMessage = () => {
    const trimmedMessage = message.trim()

    // Try to get selectedCoach from context if not available
    let effectiveSelectedCoach = selectedCoach
    if (!effectiveSelectedCoach && candidateChatManager) {
      const connectionState = candidateChatManager.getConnectionState()
      if (
        connectionState.activeCall &&
        connectionState.activeCall.coachEasyrtcid
      ) {
        // Try to find the coach in the coaches list
        const coaches = connectionState.coaches || []
        effectiveSelectedCoach = coaches.find(
          (coach) =>
            coach.easyrtcid === connectionState.activeCall.coachEasyrtcid,
        )
        /*
        console.log(
          'TextMessageEditor - Found coach from activeCall:',
          effectiveSelectedCoach,
        )
        */
      }
    }

    /*
    console.log('TextMessageEditor - handleSendMessage called:', {
      trimmedMessage,
      hasChatManager: !!candidateChatManager,
      selectedCoach,
      effectiveSelectedCoach,
      chatStatus,
    })
    */

    if (!trimmedMessage || !candidateChatManager || !effectiveSelectedCoach) {
      /*
      console.log('TextMessageEditor - Cannot send message:', {
        hasMessage: !!trimmedMessage,
        hasChatManager: !!candidateChatManager,
        hasSelectedCoach: !!selectedCoach,
        hasEffectiveSelectedCoach: !!effectiveSelectedCoach,
      })
      */
      return
    }

    // console.log('TextMessageEditor - Sending message via chatManager')
    // Send the message
    candidateChatManager.sendMessage(trimmedMessage)

    // Clear input and reset typing state
    setMessage('')
    setIsTyping(false)

    // Reset grow-wrap data attribute for auto-resize
    if (textareaRef.current) {
      const growWrap = textareaRef.current.parentNode
      if (growWrap) {
        growWrap.dataset.replicatedValue = ''
      }
    }
  }

  // Handle Enter key press
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSendMessage()
    }
  }

  // Don't show if no coach is selected
  if (!selectedCoach) {
    return null
  }

  return (
    <div className="text-message-editor px-6 py-4">
      {/* Inject CSS styles for grow-wrap */}
      <style>{growWrapStyles}</style>

      {/* Message input area - full width */}
      <div className="text-message-editor-input-area mb-3">
        <div className="grow-wrap" data-replicated-value={message}>
          <textarea
            id="text-message-compose-input"
            className={`w-full border font-['Roboto'] text-base font-normal leading-relaxed focus:outline-none focus:ring-2 focus:ring-opacity-20 ${getInputClass()}`}
            ref={textareaRef}
            value={message}
            onChange={handleMessageChange}
            onKeyDown={handleKeyPress}
            placeholder={t('message_editor.type_message', {
              defaultValue: 'Type Message...',
            })}
            disabled={chatStatus !== 'chat_active'}
            aria-label={t('message_editor.type_message', {
              defaultValue: 'Type Message...',
            })}
          />
        </div>
      </div>

      {/* Bottom controls row - dark mode on left, attachment and send buttons on right */}
      <div className="flex items-center justify-between">
        {/* Dark mode toggle */}
        <div className="flex flex-col items-start">
          <FormControlLabel
            control={
              <Switch
                checked={isDarkMode}
                onChange={toggleDarkMode}
                size="small"
              />
            }
            label={t('message_editor.dark_mode', {
              defaultValue: 'Dark mode',
            })}
            labelPlacement="start"
            sx={{
              '& .MuiFormControlLabel-label': {
                fontSize: '0.75rem',
                color: isDarkMode ? '#ffffff' : '#111827',
              },
            }}
          />
          {!isMobile && (
            <a
              href="/coach_services/article/ways-to-utilize-coach-chat"
              target="chat_browse"
              className={`ml-auto font-roboto text-xs underline underline-offset-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-intoo-green-medium-700 ${getDarkModeClasses(
                'text-intoo-green-medium-800 hover:text-intoo-green-medium-900',
                'text-intoo-green-medium-300 hover:text-intoo-green-medium-200',
              )}`}
            >
              {t('conversation_starters.ways_to_use_link', {
                defaultValue: 'Ways to use Coach Chat',
              })}
            </a>
          )}
        </div>

        {/* Right side controls - attachment and send buttons */}
        <div className="flex items-center gap-2">
          {/* Attachment button */}
          <ChatAttachmentModal
            userId={userId}
            chatId={candidateChatManager?.chatId}
            onAttachmentUploaded={handleAttachmentUploaded}
          />

          {/* Send button — CC-UX-024: match coach-side tooltip copy */}
          <Tooltip
            content={t('message_editor.send_message', {
              defaultValue: 'Send message',
            })}
            placement="top"
          >
            <Button
              variant="solid"
              color="secondary"
              size="md"
              className="h-10 rounded-[20px] px-6"
              onClick={handleSendMessage}
              disabled={!message.trim() || chatStatus !== 'chat_active'}
              title={t('message_editor.send_message', {
                defaultValue: 'Send message',
              })}
              aria-label={t('message_editor.send_message', {
                defaultValue: 'Send message',
              })}
            >
              {t('common.send', { defaultValue: 'Send' })}
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}

export default TextMessageEditor
